import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import config from "../../../config/api";
import axiosInstance from "../../../config/axiosConfig";


export const createTraining = createAsyncThunk(
  "myTrainings/createTraining",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        `${config.backendUrl}/my-training/create`,
        payload
      );
      return response.data; // contains { message, training }
    } catch (error) {
      console.error("❌ createTraining thunk error:", error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ✅ UPDATE Training
export const updateTraining = createAsyncThunk(
  "myTrainings/updateTraining",
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        `${config.backendUrl}/my-training/update/${id}`,
        payload
      );
      return response.data.updated;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ✅ GET Trainings
export const getMyTrainings = createAsyncThunk(
  "myTrainings/getMyTrainings",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `${config.backendUrl}/my-training/getll`
      );
      const payload = response?.data;
      const list = Array.isArray(payload?.trainings)
        ? payload.trainings
        : Array.isArray(payload)
          ? payload
          : [];
      console.log("[TrainingSliceDebug] getMyTrainings raw", {
        payloadType: Array.isArray(payload) ? "array" : typeof payload,
        count: list.length,
        sample: list.slice(0, 10).map((t) => ({
          id: t?._id,
          projectname: t?.projectname,
          projectid: t?.projectid ?? t?.projectId,
          quotationId: t?.quotationId ?? t?.quotationID ?? t?.quotationid,
          assignedto: t?.assignedto ?? t?.assignedTo,
          trainingcost: t?.trainingcost ?? t?.trainingCost,
          isTrainingQuoted: t?.isTrainingQuoted,
        })),
      });
      return response.data;
    } catch (error) {
      console.error("[TrainingSliceDebug] getMyTrainings error", error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ✅ DELETE Training
export const deleteTraining = createAsyncThunk(
  "myTrainings/deleteTraining",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.delete(
        `${config.backendUrl}/my-training/delete/${id}`
      );
      return { id, data: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const sendTrainingReminder = createAsyncThunk(
  "training/sendReminder",
  async (id, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post(
        `/my-training/sendReminder/${id}`
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

/** Normalize API payload `{ trainings?: [] } | []` → wrapped object with trainings array */
const coerceMyTrainingsEnvelope = (current, serverPayload) => {
  let merged =
    typeof serverPayload === "object" && serverPayload !== null && !Array.isArray(serverPayload)
      ? { ...serverPayload }
      : { trainings: [] };
  if (Array.isArray(serverPayload)) merged = { trainings: serverPayload.slice() };
  let list = Array.isArray(merged.trainings)
    ? merged.trainings.slice()
    : Array.isArray(serverPayload)
      ? serverPayload.slice()
      : unwrapTrainingsList(current?.mytrainings);
  merged.trainings = list;
  return merged;

  function unwrapTrainingsList(box) {
    if (!box) return [];
    if (Array.isArray(box?.trainings)) return [...box.trainings];
    return Array.isArray(box) ? [...box] : [];
  }
};

const mutateTrainingsList = (draft, updater) => {
  const envelope = coerceMyTrainingsEnvelope(draft, draft.mytrainings);
  const next = updater(envelope.trainings.slice());
  envelope.trainings = next;
  draft.mytrainings = envelope;
};

const myTrainingsSlice = createSlice({
  name: "myTrainings",
  initialState: {
    mytrainings: { trainings: [] },
    loading: false,
    error: null,
  },
  reducers: {},

  extraReducers: (builder) => {
    builder
    
         .addCase(createTraining.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(createTraining.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload.message;
        if (action.payload.training) {
          mutateTrainingsList(state, (list) =>
            [...list.filter((x) => x?._id !== action.payload.training?._id), action.payload.training]
          );
        }
      })
      .addCase(createTraining.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.error || "Failed to create training. Please try again.";
      })

      // ✅ Update
      .addCase(updateTraining.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateTraining.fulfilled, (state, action) => {
        state.loading = false;
        const updated = action.payload;
        if (!updated?._id) return;
        mutateTrainingsList(state, (list) =>
          list.some((x) => x?._id === updated._id)
            ? list.map((t) => (t._id === updated._id ? updated : t))
            : [...list, updated]
        );
      })
      .addCase(updateTraining.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ✅ Get
      .addCase(getMyTrainings.pending, (state) => {
        state.loading = true;
      })
      .addCase(getMyTrainings.fulfilled, (state, action) => {
        state.loading = false;
        state.mytrainings = coerceMyTrainingsEnvelope(state, action.payload);
        const payload = state.mytrainings;
        const list = Array.isArray(payload?.trainings)
          ? payload.trainings
          : Array.isArray(payload)
            ? payload
            : [];
        console.log("[TrainingSliceDebug] stored mytrainings", {
          count: list.length,
          sample: list.slice(0, 10).map((t) => ({
            id: t?._id,
            projectname: t?.projectname,
            projectid: t?.projectid ?? t?.projectId,
            quotationId: t?.quotationId ?? t?.quotationID ?? t?.quotationid,
            assignedto: t?.assignedto ?? t?.assignedTo,
            trainingcost: t?.trainingcost ?? t?.trainingCost,
            isTrainingQuoted: t?.isTrainingQuoted,
          })),
        });
      })
      .addCase(getMyTrainings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ✅ Delete
      .addCase(deleteTraining.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteTraining.fulfilled, (state, action) => {
        state.loading = false;
        mutateTrainingsList(state, (list) => list.filter((t) => t._id !== action.payload.id));
      })
      .addCase(deleteTraining.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default myTrainingsSlice.reducer;
